import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Switch } from '@heroui/react';
import { useCreateDomain } from '@/features/domains/hooks';
import { domainSchema, type DomainFormValues } from '@/features/domains/schema';

/** Add-domain modal. On success the detail page shows the DNS records to publish. */
export function DomainFormModal({ isOpen, onClose, onCreated }: {
  isOpen: boolean; onClose: () => void; onCreated?: (id: string) => void;
}) {
  const create = useCreateDomain();
  const form = useForm<DomainFormValues>({
    resolver: zodResolver(domainSchema),
    defaultValues: { domain: '', catchAllEnabled: false, dnsMode: 'external' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const created = await create.mutateAsync(values);
    form.reset();
    onClose();
    onCreated?.(created.id);
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" placement="center" backdrop="blur">
      <ModalContent className="border border-border bg-surface-raised text-fg shadow-2xl">
        <ModalHeader className="flex-col items-start gap-1 border-b border-border px-6 py-5">
          <span className="text-lg font-semibold text-fg">Add a mail domain</span>
          <span className="text-sm font-normal text-fg-muted">Connect a domain you own to Nifty Hosting. Catch-all can be set up once the domain is verified.</span>
        </ModalHeader>
        <ModalBody className="gap-5 px-6 py-5">
          <form id="domain-form" onSubmit={onSubmit} className="space-y-4" noValidate>
            <Input
              {...form.register('domain')} label="Domain name" labelPlacement="outside"
              placeholder="example.co.za" variant="bordered" autoFocus
              isInvalid={!!form.formState.errors.domain}
              errorMessage={form.formState.errors.domain?.message}
              description="Enter only the domain, without https://, www, or a page path. Example: example.co.za"
              classNames={{
                label: 'text-sm font-medium text-fg',
                inputWrapper: 'border-border bg-surface hover:bg-surface focus-within:border-brand',
                input: 'text-fg placeholder:text-fg-subtle',
                description: 'text-xs text-fg-subtle',
                errorMessage: 'text-xs text-danger',
              }}
            />
            <Controller
              control={form.control}
              name="dnsMode"
              render={({ field }) => (
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-sm font-medium text-fg">Managed DNS</p>
                    <p className="mt-1 text-xs leading-5 text-fg-muted">Optional. Nifty creates the DNS zone now, with your mail records already in it. You then set the nameservers at your registrar to ns1.niftyhosting.co.za and ns2.niftyhosting.co.za. Leave off if DNS stays with another provider.</p>
                  </div>
                  <Switch isSelected={field.value === 'managed'} onValueChange={(v) => field.onChange(v ? 'managed' : 'external')} aria-label="Enable managed DNS" />
                </div>
              )}
            />
          </form>
        </ModalBody>
        <ModalFooter className="border-t border-border px-6 py-4">
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button color="primary" type="submit" form="domain-form" isLoading={form.formState.isSubmitting}>Add domain</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
